/**
 * An organization's access dimensions and their values, and the values each org member is assigned
 * (#5358). A record type in one of the org's bases names a dimension in its `x-access` declaration.
 * Every hub toolset call carries its caller's set (#5358 PR 12, `accessSetSchema`); on a record type
 * that enforces the dimension, the call reaches only the records whose value is in that set. The org
 * owns the dimensions, so every hub and base means the same thing by one (OrgDO holds them).
 *
 * - A dimension and a value each have a fixed id and an editable label. The id is what a base's
 *   `x-access` and a call's access set name, so it never changes: relabel through `label`.
 * - A value is never deleted, only retired. WayAI cannot see which records hold a value, so a retired
 *   value still matches what it matched; it can no longer be newly assigned.
 * - An assignment names, per dimension, the value ids a member may read and may write. `['*']`
 *   reaches the dimension unrestricted, and is always explicit. A dimension it leaves out, and a
 *   member with no assignment, reach nothing on the record types that enforce that dimension.
 *
 * - A hub admin, a hub team member and a hub user are assigned values the same way, on their own
 *   HubDO rows (`HUB_PERSON_KINDS`), checked against the same org's dimensions.
 * - A hub's values and its public values are assignments too, made by an org admin only, on a
 *   production hub: a preview hub uses its production hub's, and a preview never published has none.
 *   Unset, each reaches nothing on the record types that enforce a dimension; `['*']` is explicit here too. A person with no
 *   other standing on the hub carries the public values customer-only (#5358 PR 12b,
 *   `REKOR_BOUND_ACCESS_SCOPE_HEADER`): held only on the call's customer's own records.
 * - An agent's values are an assignment in its config (HubDO's `agent` row), set by a hub admin on a
 *   preview hub or pushed with the `wayai` CLI (`access_values` in the agent's file), and carried to
 *   production by publish. Unlike every other layer, unset narrows nothing: the agent then reaches
 *   what its hub and the person it serves reach.
 * - Beside its dimensions, an org member's, hub admin's, team member's or agent's assignment may hold
 *   person access under the reserved key `$person` (wayai#5473, `PERSON_ACCESS_KEY` below).
 *
 * The ids and counts are held to the data product's caps on the access set a call carries
 * (`REKOR_MAX_ACCESS_*`), so a set built from an org's values is never one the data product refuses.
 */

import { z } from 'zod';
import { uuidSchema } from '../common';
import { AGENT_ROLE_DISPLAY, CUSTOMER_FACING_AGENT_ROLES } from '../../constants';
import {
  REKOR_MAX_ACCESS_DIMENSIONS,
  REKOR_MAX_ACCESS_ID_LENGTH,
  REKOR_MAX_ACCESS_VALUES,
} from '../rekor-attestation';

/** Reaches a dimension unrestricted: never an id, and only ever a list's one entry. */
export const ACCESS_UNRESTRICTED = '*';

/**
 * A dimension's or a value's id: ASCII letters, digits, `_` and `-`, starting with a letter or digit.
 * It rides an HTTP header as JSON, hence ASCII. It holds no `:` or space, so no id reads as a
 * date-time, which a list filter compares as an instant rather than as text; and no `*`.
 */
export const ACCESS_ID_REGEX = /^[A-Za-z0-9][A-Za-z0-9_-]*$/;
export const ACCESS_ID_MAX_LENGTH = REKOR_MAX_ACCESS_ID_LENGTH;
/** The dimensions one org may define: every one of them may be in one call's set. */
export const MAX_ACCESS_DIMENSIONS_PER_ORG = REKOR_MAX_ACCESS_DIMENSIONS;
/** The values one dimension may hold, retired ones included: a retired value may still be assigned. */
export const MAX_ACCESS_VALUES_PER_DIMENSION = REKOR_MAX_ACCESS_VALUES;
export const ACCESS_LABEL_MAX_LENGTH = 100;

export const accessIdSchema = z.string()
  .min(1)
  .max(ACCESS_ID_MAX_LENGTH)
  .regex(ACCESS_ID_REGEX, { message: 'Must be ASCII letters, digits, "_" or "-", starting with a letter or digit (e.g. "class", "7A")' });

export const accessLabelSchema = z.string().trim().min(1).max(ACCESS_LABEL_MAX_LENGTH);

/** One dimension's list: value ids, or `['*']` alone. */
const accessValueListSchema = z.array(z.union([accessIdSchema, z.literal(ACCESS_UNRESTRICTED)]))
  .max(MAX_ACCESS_VALUES_PER_DIMENSION)
  .refine((list) => !list.includes(ACCESS_UNRESTRICTED) || list.length === 1, {
    message: `"${ACCESS_UNRESTRICTED}" reaches the dimension unrestricted, so it stands alone in its list`,
  });

const accessListsSchema = z.object({ read: accessValueListSchema, write: accessValueListSchema }).strict();

/**
 * Per dimension, the values a member may read and may write. Which ids an org holds, and that write
 * stays within read, are checked against the org's dimensions where the assignment is stored.
 */
export const accessAssignmentSchema = z.record(accessIdSchema, accessListsSchema)
  .refine((assignment) => Object.keys(assignment).length <= MAX_ACCESS_DIMENSIONS_PER_ORG, {
    message: `An assignment names at most ${MAX_ACCESS_DIMENSIONS_PER_ORG} dimensions`,
  });

export type AccessAssignment = z.infer<typeof accessAssignmentSchema>;

// =============================================================================
// PERSON ACCESS (`$person`, wayai#5473 §3)
// =============================================================================

/**
 * The reserved key an assignment holds person access under, beside its dimensions: `*`, every
 * person's records, read and written (`{ read: ['*'], write: ['*'] }`, its one value). No dimension
 * can take the key, since a dimension id starts with a letter or digit (`ACCESS_ID_REGEX`).
 *
 * - Assigned explicitly, never automatic, and only to org members, hub admins, team members (per
 *   team) and agents (`heldAccessAssignmentSchema`). A hub user, a hub's values and its public values
 *   never hold it (`personlessAssignmentSchema`), and neither does an agent whose role is
 *   customer-facing (`isCustomerFacingAgentRole`), at every agent write, publish and sync included.
 * - It is no dimension: an access set never carries it (`accessSetSchema` refuses the key), so every
 *   reader that works one out reads an assignment without it (`withoutPersonAccess`).
 * - Its lists have a dimension's shape, so a reader that only lists an assignment's keys (an older
 *   web page or CLI) shows it as one more key instead of failing on it.
 */
export const PERSON_ACCESS_KEY = '$person';

/** Whether `lists` is person access's one value: `*` in both lists. */
export function isPersonAccessAll(lists: { read: readonly string[]; write: readonly string[] } | undefined): boolean {
  return lists !== undefined
    && lists.read.length === 1 && lists.read[0] === ACCESS_UNRESTRICTED
    && lists.write.length === 1 && lists.write[0] === ACCESS_UNRESTRICTED;
}

/** Person access's one value, a fresh copy each time. */
export function personAccessAll(): AccessAssignment[string] {
  return { read: [ACCESS_UNRESTRICTED], write: [ACCESS_UNRESTRICTED] };
}

/** Whether an assignment holds person access (`$person`). Read by own keys only. */
export function holdsPersonAccess(assignment: AccessAssignment | null | undefined): boolean {
  return assignment != null && Object.hasOwn(assignment, PERSON_ACCESS_KEY);
}

/** The assignment's dimensions alone: what an access set is worked out from. */
export function withoutPersonAccess(assignment: AccessAssignment): AccessAssignment {
  if (!holdsPersonAccess(assignment)) return assignment;
  const dimensions: AccessAssignment = {};
  for (const [key, lists] of Object.entries(assignment)) if (key !== PERSON_ACCESS_KEY) dimensions[key] = lists;
  return dimensions;
}

/** The assignment with person access given (`on`) or taken away, its dimensions as they are. */
export function withPersonAccess(assignment: AccessAssignment, on: boolean): AccessAssignment {
  const dimensions = withoutPersonAccess(assignment);
  return on ? { ...dimensions, [PERSON_ACCESS_KEY]: personAccessAll() } : dimensions;
}

const heldAccessKeySchema = z.string().refine(
  (key) => key === PERSON_ACCESS_KEY || accessIdSchema.safeParse(key).success,
  { message: `Must be "${PERSON_ACCESS_KEY}" or a dimension id: ASCII letters, digits, "_" or "-", starting with a letter or digit (e.g. "class", "7A")` },
);

/**
 * An assignment as a holder of person access takes it: `accessAssignmentSchema`'s, and `$person`
 * beside its dimensions, holding only `{ read: ['*'], write: ['*'] }`. It counts against no
 * dimension cap.
 */
export const heldAccessAssignmentSchema = z.record(heldAccessKeySchema, accessListsSchema).superRefine((assignment, ctx) => {
  if (holdsPersonAccess(assignment) && !isPersonAccessAll(assignment[PERSON_ACCESS_KEY])) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [PERSON_ACCESS_KEY],
      message: `"${PERSON_ACCESS_KEY}" holds only { "read": ["${ACCESS_UNRESTRICTED}"], "write": ["${ACCESS_UNRESTRICTED}"] }: every person's records`,
    });
  }
  if (Object.keys(withoutPersonAccess(assignment)).length > MAX_ACCESS_DIMENSIONS_PER_ORG) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: `An assignment names at most ${MAX_ACCESS_DIMENSIONS_PER_ORG} dimensions` });
  }
});

/** The refusal of person access on `holders` that never take it, as the contract and the DOs answer it. */
export function personAccessRefusal(holders: string): string {
  return `${holders} never hold "${PERSON_ACCESS_KEY}"`;
}

/** The holders that never take person access, as a refusal names them. */
export function personlessAssignmentSchema(holders: string) {
  return heldAccessAssignmentSchema.refine((assignment) => !holdsPersonAccess(assignment), {
    message: personAccessRefusal(holders),
    path: [PERSON_ACCESS_KEY],
  });
}

/**
 * The refusal of person access on a customer-facing agent (`isCustomerFacingAgentRole`,
 * `constants.ts`), at any agent write.
 */
export const AGENT_PERSON_ACCESS_REFUSAL = `A customer-facing agent (${CUSTOMER_FACING_AGENT_ROLES.map((role) => AGENT_ROLE_DISPLAY[role]).join(', ')}) never holds "${PERSON_ACCESS_KEY}" in its access values: remove it, or give the agent another role`;

/** Whether each dimension's write list stays within its read list (a `*` read holds any write). */
export function writeWithinRead(assignment: AccessAssignment): boolean {
  return Object.values(assignment).every(({ read, write }) =>
    read.includes(ACCESS_UNRESTRICTED) || (!write.includes(ACCESS_UNRESTRICTED) && write.every((id) => read.includes(id))));
}

/**
 * The access set a bound toolset call carries (`REKOR_BOUND_ACCESS_HEADER`, #5358): an assignment's
 * shape, every write list within its read list. WayAI works it out per call from every layer that
 * applies (`apps/backend/src/rekor/access-set.ts`). A dimension it leaves out reaches nothing on the
 * record types that enforce that dimension, so `{}` reaches nothing on any of them.
 */
export const accessSetSchema = accessAssignmentSchema.refine(writeWithinRead, {
  message: 'An access set writes only what it reads',
});

export type AccessSet = z.infer<typeof accessSetSchema>;

/** The header text of an access set: compact JSON, the form the header's caps are stated over. */
export function encodeAccessSet(set: AccessSet): string {
  return JSON.stringify(set);
}

/** Whether `raw` is the header text of an access set. */
export function isEncodedAccessSet(raw: string): boolean {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return false;
  }
  return accessSetSchema.safeParse(parsed).success;
}

// =============================================================================
// REQUEST SCHEMAS
// =============================================================================

// `id` as `organizations.ts`' `orgIdParam` takes it, not imported from there: OrgDO compiles this
// module, and that one would bring the org routes' schemas into its closure.
export const accessDimensionParam = z.object({
  id: uuidSchema,
  dimensionId: accessIdSchema,
});

export const accessValueParam = accessDimensionParam.extend({
  valueId: accessIdSchema,
});

/**
 * A member's `member_grant.user_id`: a WayAI user id, an invitee's placeholder UUID, or a legacy row's
 * email (`admin-user-ops.ts` still reads those), so not `USER_ID_REGEX`.
 */
export const orgMemberParam = z.object({
  id: uuidSchema,
  userId: z.string().min(1).max(256),
});

/** A hub of the org, by id. */
export const orgHubParam = z.object({
  id: uuidSchema,
  hubId: uuidSchema,
});

export const createAccessDimensionBody = z.object({
  dimension_id: accessIdSchema,
  label: accessLabelSchema,
});

/** The id is absent by design: it is fixed at creation. */
export const updateAccessDimensionBody = z.object({
  label: accessLabelSchema,
});

export const createAccessValueBody = z.object({
  value_id: accessIdSchema,
  label: accessLabelSchema,
});

/** Relabel, retire (`retired: true`) or restore (`retired: false`). The id is fixed at creation. */
export const updateAccessValueBody = z.object({
  label: accessLabelSchema.optional(),
  retired: z.boolean().optional(),
}).refine((body) => body.label !== undefined || body.retired !== undefined, {
  message: 'Nothing to change: pass label, retired, or both',
});

/** An org member's values, a hub admin's and a hub team member's: they may hold `$person`. */
export const setMemberAccessValuesBody = z.object({
  values: heldAccessAssignmentSchema,
});

/**
 * Who on a hub holds assigned values, each on its own HubDO row: a hub admin (`hub_admin_user`), a
 * hub team member (`hub_team_user`, one row per person per team) and a hub user (`hub_user`, a
 * `grant` row only). Hub admins' values are an org admin's to set; the other two a hub admin's.
 */
export const HUB_PERSON_KINDS = ['hub_admin', 'hub_team_member', 'hub_user'] as const;
export type HubPersonKind = (typeof HUB_PERSON_KINDS)[number];

/**
 * A person's kinds on a hub, in the order the first match wins (#5358 PR 12): an org member, then the
 * hub's kinds, then everyone else (`public`), who carries the hub's public values.
 */
export const ACCESS_PERSON_KINDS = ['org_member', ...HUB_PERSON_KINDS, 'public'] as const;
export type AccessPersonKind = (typeof ACCESS_PERSON_KINDS)[number];

/** What a missing row of each kind is called. */
export const HUB_PERSON_NOUNS: Readonly<Record<HubPersonKind, string>> = {
  hub_admin: 'Hub admin',
  hub_team_member: 'Hub team member',
  hub_user: 'Hub user',
};

/** Whether an assignment names `*` in any list. */
export function assignmentHasUnrestricted(assignment: AccessAssignment): boolean {
  return Object.values(assignment).some((lists) => lists.read.includes(ACCESS_UNRESTRICTED) || lists.write.includes(ACCESS_UNRESTRICTED));
}

/**
 * A hub user's values: an org member's shape, never `*`. A hub user is the kind that must stay
 * scoped (a parent, a student), so reaching a dimension unrestricted is never theirs.
 */
export const setHubUserAccessValuesBody = z.object({
  values: personlessAssignmentSchema("A hub user's values").refine((assignment) => !assignmentHasUnrestricted(assignment), {
    message: `A hub user's values never hold "${ACCESS_UNRESTRICTED}"`,
  }),
});

/** A hub's values, or its public values: a member's shape, never `$person`. */
export const setHubAccessValuesBody = z.object({
  values: personlessAssignmentSchema("A hub's values and its public values"),
});

/**
 * An agent's values: the same whole assignment a member's is, or null, which clears them, so the
 * agent narrows nothing again. `$person` is refused on a customer-facing agent where the agent's role
 * is read (`AGENT_PERSON_ACCESS_REFUSAL`).
 */
export const setAgentAccessValuesBody = z.object({
  values: heldAccessAssignmentSchema.nullable(),
});

export type CreateAccessDimensionInput = z.infer<typeof createAccessDimensionBody>;
export type UpdateAccessDimensionInput = z.infer<typeof updateAccessDimensionBody>;
export type CreateAccessValueInput = z.infer<typeof createAccessValueBody>;
export type UpdateAccessValueInput = z.infer<typeof updateAccessValueBody>;

// =============================================================================
// RESPONSE TYPES
// =============================================================================

export interface AccessValueResponse {
  value_id: string;
  label: string;
  /** When the value was retired; null while it can be assigned. */
  retired_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface AccessDimensionResponse {
  dimension_id: string;
  label: string;
  created_at: string;
  updated_at: string;
  /** Every value of the dimension, retired ones included, by id. */
  values: AccessValueResponse[];
}

export interface ListAccessDimensionsResponse {
  data: AccessDimensionResponse[];
}

export interface AccessDimensionMutationResponse {
  data: AccessDimensionResponse;
}

export interface AccessValueMutationResponse {
  data: AccessValueResponse;
}

export interface MemberAccessValues {
  user_id: string;
  /**
   * Null when the member has never been assigned values: they reach nothing on the record types that
   * enforce a dimension.
   */
  values: AccessAssignment | null;
}

export interface MemberAccessValuesResponse {
  data: MemberAccessValues;
}

/** One hub admin's, hub team member's or hub user's values, by the id of the row that holds them. */
export interface HubPersonAccessValues {
  kind: HubPersonKind;
  /** `hub_admin_user_id`, `hub_team_user_id` or `hub_user_id`. */
  id: string;
  /**
   * Null when the row has never been assigned values: that person reaches nothing through it on the
   * record types that enforce a dimension.
   */
  values: AccessAssignment | null;
}

export interface HubPersonAccessValuesResponse {
  data: HubPersonAccessValues;
}

/**
 * A hub's values and its public values (#5358), held on the record types that enforce a dimension: a
 * hub's values bound every call its turns make, and its public values are what a person with no other
 * standing on the hub (a channel contact, say) reaches, on record types customer scope also narrows
 * only, sent as a customer-only set (#5358 PR 12b, `REKOR_BOUND_ACCESS_SCOPE_HEADER`).
 */
export interface HubAccessValues {
  /** The hub asked about. */
  hub_id: string;
  /**
   * The production hub whose values apply: `hub_id` itself on production, its production hub on a
   * preview, and null on a preview never published, which has none.
   */
  values_hub_id: string | null;
  /** Null while unset, which reaches nothing on the record types that enforce a dimension. */
  values: AccessAssignment | null;
  /** Null while unset, which reaches nothing on the record types that enforce a dimension. */
  public_values: AccessAssignment | null;
}

export interface HubAccessValuesResponse {
  data: HubAccessValues;
}

/** An agent's values (#5358), from its config. */
export interface AgentAccessValues {
  agent_id: string;
  /**
   * Null when unset: the agent narrows nothing. Otherwise, on the record types that enforce a
   * dimension, a turn of the agent reaches only what these values also allow.
   */
  values: AccessAssignment | null;
}

export interface AgentAccessValuesResponse {
  data: AgentAccessValues;
}
